'use client'

import { AnimatePresence, m } from 'framer-motion'
import { Building2, Check, Mail, MessageSquare, Phone, Send, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { FinalCtaBenefits } from '@/components/sections/final-cta/FinalCtaBenefits'
import { FinalCtaGoalSelect } from '@/components/sections/final-cta/FinalCtaGoalSelect'
import { FinalCtaSuccess } from '@/components/sections/final-cta/FinalCtaSuccess'
import { EMPTY_FINAL_CTA_FORM, type FinalCtaFormData } from '@/components/sections/final-cta/model'
import { useLeadForm } from '@/hooks/useLeadForm'

export default function FinalCTA() {
  const [formData, setFormData] = useState<FinalCtaFormData>({ ...EMPTY_FINAL_CTA_FORM })
  const [agreed, setAgreed] = useState(false)
  const { isSubmitting, isSubmitted, error, submitLead, resetLeadStatus } = useLeadForm()
  const [isGoalOpen, setIsGoalOpen] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsGoalOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!agreed) return

    await submitLead({
      source: 'Форма в блоке «Следующий шаг — за вами»',
      ...formData,
    })
  }

  function resetFormState() {
    resetLeadStatus()
    setFormData({ ...EMPTY_FINAL_CTA_FORM })
    setAgreed(false)
  }

  return (
    <section id="contact-form" className="home-section-compact relative overflow-hidden">
      <div className="home-container relative z-10">
        <div className="grid items-start gap-8 lg:grid-cols-12 lg:gap-16">
          <FinalCtaBenefits />

          <m.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="relative lg:col-span-7"
          >
            <div className="absolute -inset-0.5 rounded-[40px] bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-cyan-500/50 opacity-75 blur-md" />

            <div className="relative w-full rounded-[40px] bg-[#0a0f1a]/90 p-8 backdrop-blur-xl lg:p-10">
              <AnimatePresence mode="wait">
                {isSubmitted ? (
                  <FinalCtaSuccess onReset={resetFormState} />
                ) : (
                  <m.form key="form" onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm text-gray-400">Имя *</label>
                        <m.div
                          className="relative"
                          animate={{
                            boxShadow:
                              focusedField === 'name'
                                ? '0 0 20px rgba(99, 102, 241, 0.3)'
                                : '0 0 0px transparent',
                          }}
                          transition={{ duration: 0.2 }}
                          style={{ borderRadius: '1rem' }}
                        >
                          <User
                            className={`absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 transition-colors ${focusedField === 'name' ? 'text-blue-400' : 'text-gray-500'}`}
                          />
                          <input
                            type="text"
                            required
                            data-testid="lead-name-input"
                            autoComplete="off"
                            value={formData.name}
                            onChange={(event) =>
                              setFormData({ ...formData, name: event.target.value })
                            }
                            onFocus={() => setFocusedField('name')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="Ваше имя"
                            className="w-full rounded-2xl border border-white/20 bg-white/10 py-4 pr-4 pl-12 text-white transition-all placeholder:text-gray-400 focus:border-blue-500/50 focus:outline-none"
                          />
                        </m.div>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm text-gray-400">Компания</label>
                        <m.div
                          className="relative"
                          animate={{
                            boxShadow:
                              focusedField === 'company'
                                ? '0 0 20px rgba(99, 102, 241, 0.3)'
                                : '0 0 0px transparent',
                          }}
                          transition={{ duration: 0.2 }}
                          style={{ borderRadius: '1rem' }}
                        >
                          <Building2
                            className={`absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 transition-colors ${focusedField === 'company' ? 'text-blue-400' : 'text-gray-500'}`}
                          />
                          <input
                            type="text"
                            data-testid="lead-company-input"
                            autoComplete="off"
                            value={formData.company}
                            onChange={(event) =>
                              setFormData({ ...formData, company: event.target.value })
                            }
                            onFocus={() => setFocusedField('company')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="Название компании"
                            className="w-full rounded-2xl border border-white/20 bg-white/10 py-4 pr-4 pl-12 text-white transition-all placeholder:text-gray-400 focus:border-blue-500/50 focus:outline-none"
                          />
                        </m.div>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm text-gray-400">Email *</label>
                        <m.div
                          className="relative"
                          animate={{
                            boxShadow:
                              focusedField === 'email'
                                ? '0 0 20px rgba(99, 102, 241, 0.3)'
                                : '0 0 0px transparent',
                          }}
                          transition={{ duration: 0.2 }}
                          style={{ borderRadius: '1rem' }}
                        >
                          <Mail
                            className={`absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 transition-colors ${focusedField === 'email' ? 'text-blue-400' : 'text-gray-500'}`}
                          />
                          <input
                            type="email"
                            required
                            data-testid="lead-email-input"
                            autoComplete="off"
                            value={formData.email}
                            onChange={(event) =>
                              setFormData({ ...formData, email: event.target.value })
                            }
                            onFocus={() => setFocusedField('email')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="email@example.com"
                            className="w-full rounded-2xl border border-white/20 bg-white/10 py-4 pr-4 pl-12 text-white transition-all placeholder:text-gray-400 focus:border-blue-500/50 focus:outline-none"
                          />
                        </m.div>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm text-gray-400">Телефон</label>
                        <m.div
                          className="relative"
                          animate={{
                            boxShadow:
                              focusedField === 'phone'
                                ? '0 0 20px rgba(99, 102, 241, 0.3)'
                                : '0 0 0px transparent',
                          }}
                          transition={{ duration: 0.2 }}
                          style={{ borderRadius: '1rem' }}
                        >
                          <Phone
                            className={`absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 transition-colors ${focusedField === 'phone' ? 'text-blue-400' : 'text-gray-500'}`}
                          />
                          <input
                            type="tel"
                            data-testid="lead-phone-input"
                            autoComplete="off"
                            value={formData.phone}
                            onChange={(event) =>
                              setFormData({ ...formData, phone: event.target.value })
                            }
                            onFocus={() => setFocusedField('phone')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="+7 (___) ___-__-__"
                            className="w-full rounded-2xl border border-white/20 bg-white/10 py-4 pr-4 pl-12 text-white transition-all placeholder:text-gray-400 focus:border-blue-500/50 focus:outline-none"
                          />
                        </m.div>
                      </div>
                    </div>

                    <FinalCtaGoalSelect
                      dropdownRef={dropdownRef}
                      value={formData.goal}
                      isOpen={isGoalOpen}
                      onToggle={() => setIsGoalOpen((prev) => !prev)}
                      onSelect={(goal) => {
                        setFormData({ ...formData, goal })
                        setIsGoalOpen(false)
                      }}
                    />

                    <div>
                      <label className="mb-2 block text-sm text-gray-400">Сообщение</label>
                      <m.div
                        className="relative"
                        animate={{
                          boxShadow:
                            focusedField === 'message'
                              ? '0 0 20px rgba(99, 102, 241, 0.3)'
                              : '0 0 0px transparent',
                        }}
                        transition={{ duration: 0.2 }}
                        style={{ borderRadius: '1rem' }}
                      >
                        <MessageSquare
                          className={`absolute top-4 left-4 h-5 w-5 transition-colors ${focusedField === 'message' ? 'text-blue-400' : 'text-gray-500'}`}
                        />
                        <textarea
                          data-testid="lead-message-input"
                          autoComplete="off"
                          value={formData.message}
                          onChange={(event) =>
                            setFormData({ ...formData, message: event.target.value })
                          }
                          onFocus={() => setFocusedField('message')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="Расскажите подробнее о проекте..."
                          rows={3}
                          className="w-full resize-none rounded-2xl border border-white/20 bg-white/10 py-4 pr-4 pl-12 text-white transition-all placeholder:text-gray-400 focus:border-blue-500/50 focus:outline-none"
                        />
                      </m.div>
                    </div>

                    <input
                      type="text"
                      data-testid="lead-honeypot-input"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website}
                      onChange={(event) =>
                        setFormData({ ...formData, website: event.target.value })
                      }
                      className="hidden"
                      aria-hidden
                    />

                    <label className="group flex cursor-pointer items-center gap-3 py-2">
                      <div className="relative">
                        <input
                          type="checkbox"
                          data-testid="lead-agree-checkbox"
                          checked={agreed}
                          onChange={(event) => setAgreed(event.target.checked)}
                          className="peer sr-only"
                        />
                        <m.div
                          whileTap={{ scale: 0.9 }}
                          className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-all ${
                            agreed
                              ? 'border-transparent bg-gradient-to-r from-blue-500 to-purple-500'
                              : 'border-white/20 bg-transparent'
                          }`}
                        >
                          <AnimatePresence>
                            {agreed ? (
                              <m.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.15 }}
                              >
                                <Check className="h-3 w-3 text-white" strokeWidth={3} />
                              </m.div>
                            ) : null}
                          </AnimatePresence>
                        </m.div>
                      </div>
                      <span className="text-sm text-gray-400 transition-colors group-hover:text-gray-300">
                        Соглашаюсь с{' '}
                        <a href="/privacy" className="text-blue-400 hover:underline">
                          политикой конфиденциальности
                        </a>
                      </span>
                    </label>

                    <m.button
                      type="submit"
                      data-testid="lead-submit-button"
                      disabled={isSubmitting || !agreed}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 px-8 py-5 text-lg font-semibold transition-all duration-300 hover:shadow-[0_0_60px_rgba(99,102,241,0.3)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      ) : (
                        <>
                          Отправить заявку
                          <Send className="h-5 w-5" />
                        </>
                      )}
                    </m.button>

                    {error ? (
                      <p
                        data-testid="lead-error-message"
                        className="pt-1 text-center text-sm text-red-400"
                      >
                        {error}
                      </p>
                    ) : null}
                  </m.form>
                )}
              </AnimatePresence>
            </div>
          </m.div>
        </div>
      </div>
    </section>
  )
}
